import {
  InventoryUpdateReason,
  POSITIVE_REASONS,
  NEGATIVE_REASONS,
  InventoryStockUpdate,
} from "../../../Interfaces/Warehouse";
import { Product } from "../../../Interfaces/Product";
import fetchWithAuth from "../../../services/fetchWithAuth";
import config from "../../../config";

interface ValidateQuantityParams {
  quantity: number | string;
  reasoning: InventoryUpdateReason;
  setQuantityError: React.Dispatch<React.SetStateAction<string>>;
}

export const validateQuantity = ({
  quantity,
  reasoning,
  setQuantityError,
}: ValidateQuantityParams): boolean => {
  setQuantityError("");
  if (POSITIVE_REASONS.includes(reasoning) && Number(quantity) < 0) {
    setQuantityError(
      `ERROR! Reason ${reasoning} indicates a POSITIVE addition to quantity but you inputted ${quantity}`
    );
    return false;
  }
  if (NEGATIVE_REASONS.includes(reasoning) && Number(quantity) > 0) {
    setQuantityError(
      `ERROR! Reason ${reasoning} indicates a NEGATIVE addition to quantity but you inputted ${quantity}`
    );
    return false;
  }
  if (Number(quantity) === 0) {
    setQuantityError("ERROR! Quantity cannot be 0!");
    return false;
  }
  return true;
};

interface ValidateVerboseReasoningParams {
  reasoning: InventoryUpdateReason;
  verboseReasoning: string;
  setVerboseReasoningError: React.Dispatch<React.SetStateAction<string>>;
}

const validateVerboseReasoning = ({
  reasoning,
  verboseReasoning,
  setVerboseReasoningError,
}: ValidateVerboseReasoningParams): boolean => {
  setVerboseReasoningError("");
  if (reasoning === "OTHER" && verboseReasoning.trim() === "") {
    setVerboseReasoningError(
      "ERROR! Reasoning cannot be other and have an empty explanation"
    );
    return false;
  }
  return true;
};

export interface ValidateAllParams
  extends ValidateQuantityParams,
    ValidateVerboseReasoningParams {}

export const validateInput = (params: ValidateAllParams): boolean => {
  const verboseValid = validateVerboseReasoning(params);
  const quantityValid = validateQuantity(params);
  return verboseValid && quantityValid;
};

interface SubmitOrderParams {
  orderToSubmit: InventoryStockUpdate[];
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSuccessfulSubmission: React.Dispatch<React.SetStateAction<boolean>>;
}

export const submitOrder = async ({
  orderToSubmit,
  setLoading,
  setSuccessfulSubmission,
}: SubmitOrderParams) => {
  try {
    setLoading(true);
    const response = await fetchWithAuth(
      `${config.API_BASE_URL}inventory-stock/update-counts/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderToSubmit),
      }
    );
    if (!response.ok) {
      throw new Error("Failed to submit order");
    }
    setSuccessfulSubmission(true);

    const result = await response.json();
  } catch (error) {
    console.error("Error submitting order:", error);
    alert("Failed to submit order. Please try again.");
  } finally {
    setLoading(false);
  }
};
